import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { dialogFormLayoutSx } from "../../lib/ui/dialogFormLayout";

export function DrawerForm({ children }: { children: ReactNode }) {
  return <Box sx={dialogFormLayoutSx}>{children}</Box>;
}

export function DrawerFormSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="h3"
            sx={{
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "text.secondary",
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export function DrawerFormAddButton({
  onClick,
  label,
}: {
  onClick: () => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const buttonLabel = label ?? t("common.add");
  return (
    <Button
      type="button"
      variant="text"
      size="small"
      onClick={onClick}
      startIcon={<Icon sx={{ fontSize: "16px !important" }}>add</Icon>}
      sx={{ minWidth: 0, px: 0.75, textTransform: "none", fontWeight: 600 }}
    >
      {buttonLabel}
    </Button>
  );
}

export function DrawerFormRow({ children }: { children: ReactNode }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", mb: 0.75, "&:last-of-type": { mb: 0 } }}>
      {children}
    </Box>
  );
}
